import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle, User, KeyRound, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { resetAuthExpired } from '@/lib/api';

export function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  
  const { login, verify2FA, isAuthenticated, authError, clearError, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from || '/';

  useEffect(() => {
    if (authError) {
      setError(authError);
      clearError();
    }
  }, [authError, clearError]);

  useEffect(() => {
    resetAuthExpired();
  }, []);

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await login(username, password);
      
      // Check if 2FA is required
      if (result?.requires_2fa) {
        setRequires2FA(true);
        setTempToken(result.temp_token);
        setIsLoading(false);
        return;
      }
      
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await verify2FA(tempToken, totpCode);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Invalid verification code.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setTempToken('');
    setTotpCode('');
    setPassword('');
    setError('');
  };

  return (
    <div 
      className="relative flex min-h-screen items-center justify-center"
      data-testid="login-page"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(/login-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      {/* Dark Overlay */}
      <div className="absolute inset-0 z-0 bg-black/70" />

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        {/* Glass Card */}
        <div className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-xl p-8 shadow-2xl">
          
          {/* Logo */}
          <div className="flex flex-col items-center mb-6">
            <img 
              src="/railstream-logo-transparent.png" 
              alt="RailStream" 
              className="h-24 w-auto mb-3"
            />
            <h2 className="text-2xl font-bold text-white tracking-wide">RAILSTREAM</h2>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <p className="text-white/80 text-sm uppercase tracking-widest mb-1">Welcome to</p>
            <h1 className="text-3xl font-bold text-white tracking-tight">RAILSTREAM TOWER</h1>
            <p className="text-white/70 text-sm mt-2">Admin Control Panel</p>
          </div>

          {/* Error Message */}
          {error && (
            <div 
              className="flex items-center gap-2 rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-400 mb-6"
              data-testid="login-error"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!requires2FA ? (
            /* Password Login Form */
            <form onSubmit={handlePasswordSubmit} className="space-y-5">
              {/* Username Field */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">
                  <User className="h-5 w-5" />
                </div>
                <Input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="username"
                  className="h-12 pl-11 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-primary focus:ring-primary"
                  data-testid="username-input"
                />
              </div>

              {/* Password Field */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">
                  <KeyRound className="h-5 w-5" />
                </div>
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                  className="h-12 pl-11 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-primary focus:ring-primary"
                  data-testid="password-input"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="h-12 w-full text-base font-semibold bg-primary hover:bg-primary/90 text-white"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          ) : (
            /* 2FA Verification Form */
            <form onSubmit={handle2FASubmit} className="space-y-5">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 mb-4">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <p className="text-white/70 text-sm">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>

              {/* TOTP Code Field */}
              <div className="relative">
                <Input
                  type="text"
                  placeholder="000000"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  disabled={isLoading}
                  autoComplete="one-time-code"
                  className="h-14 text-center text-2xl font-mono tracking-[0.5em] bg-white/5 border-white/10 text-white placeholder:text-white/20 focus:border-primary focus:ring-primary"
                  data-testid="totp-input"
                />
              </div>

              {/* Verify Button */}
              <Button
                type="submit"
                className="h-12 w-full text-base font-semibold bg-primary hover:bg-primary/90 text-white"
                disabled={isLoading || totpCode.length !== 6}
                data-testid="verify-2fa-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </Button>

              {/* Back Link */}
              <button
                type="button"
                onClick={handleBackToLogin}
                className="w-full text-center text-sm text-white/50 hover:text-white/70 transition-colors"
              >
                ← Back to login
              </button>
            </form>
          )}

          {/* Footer */}
          <p className="mt-8 text-center text-xs text-white/70">
            Access restricted to authorized administrators only.
          </p>
        </div>

        {/* Copyright */}
        <p className="mt-6 text-center text-xs text-white/60">
          © {new Date().getFullYear()} RailStream, LLC. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default Login;
